import { formatPhoneNumber } from "@equidefi/shared/helpers/string";
import * as Yup from "yup";
import { CurrencyFormatter, NumberFormatter } from "@equidefi/i18n";
import {
  calculateMaxSubscriptionAmount,
  formatSecurities,
} from "@equidefi/shared";

const MAX_PAYMENT_AMOUNT = 15_000_000;

export const subscriptionSchema = ({
  offering,
  isOfferingRegA,
  investments = [],
}) => {
  const unitPrice = Number(offering?.unit_price) || 1;
  const minUnits = offering?.investor_minimum_units;
  const minAmount = offering?.investor_minimum_units * unitPrice;

  const standardSchema = Yup.object({
    accredited: Yup.boolean().required().oneOf([true]),
    broker_dealer: Yup.boolean().required(
      "Please indicate if you are affiliated with a broker dealer",
    ),
    quantity: Yup.number()
      .test({
        message: "Quantity is required",
        test: (quantity) => !!quantity,
      })
      .test({
        message: "Quantity must not be a decimal",
        test: (quantity) => Number(quantity) === Math.round(quantity),
      })
      .test({
        message: `Minimum ${formatSecurities({
          quantity: minUnits,
          offering,
        }).toString()} required`,
        test: (quantity) => !(quantity > 0 && quantity < minUnits),
      })
      .max(
        MAX_PAYMENT_AMOUNT / unitPrice,
        `If you would like to invest more than ${CurrencyFormatter.format(
          MAX_PAYMENT_AMOUNT,
        )}, please contact the issuer at ${formatPhoneNumber(
          offering?.issuer?.phone,
        )}`,
      ),
    amount: Yup.number()
      .test({
        message: "Amount is required",
        test: (amount) => !!amount,
      })
      .test({
        message: `Minimum ${CurrencyFormatter.format(
          minUnits * unitPrice,
        )} investment required`,
        test: (value) => !(value > 0 && value < minAmount),
      }),
    selectedBroker: Yup.object({
      label: Yup.string().nullable(),
      value: Yup.object().nullable(),
    }).nullable(),
  });

  const incomeSchema = Yup.object({
    accredited: Yup.boolean(),
    broker_dealer: Yup.boolean().required(
      "Please indicate if you are affiliated with a broker dealer",
    ),
    annual_income: Yup.number("Must be a number")
      .required("Annual income is required")
      .min(1, "Annual income must be greater than $0"),
    net_worth: Yup.number()
      .required("Net worth is required")
      .min(1, "Net worth must be greater than $0"),
    quantity: Yup.number()
      .required("Quantity is required")
      .min(
        minUnits,
        `Minimum ${NumberFormatter.format(minUnits)} shares required`,
      )
      .test({
        message: "Quantity must not be a decimal",
        test: (quantity) => Number(quantity) === Math.round(quantity),
      })
      .when(
        ["accredited", "annual_income", "net_worth"],
        (accredited, annualIncome, netWorth, schema) => {
          if (accredited === true || accredited === "true") return schema;

          const maxAmount = calculateMaxSubscriptionAmount({
            annualIncome,
            netWorth,
            investments,
          });

          const maxQuantity = Math.round(maxAmount / unitPrice);
          const maxQunatityFormatted = NumberFormatter.format(maxQuantity);

          if (maxAmount >= MAX_PAYMENT_AMOUNT) {
            return schema.max(
              MAX_PAYMENT_AMOUNT / unitPrice,
              `If you would like to invest more than ${CurrencyFormatter.format(
                MAX_PAYMENT_AMOUNT,
              )}, please contact the issuer at ${formatPhoneNumber(
                offering?.issuer?.phone,
              )}`,
            );
          }

          if (maxQuantity === 0) {
            return schema.max(
              maxQuantity,
              "Based on your stated annual income and net worth, you can not purchase shares in this offering.",
            );
          }

          if (maxQuantity < minUnits) {
            if (investments.length > 0) {
              return schema.max(
                maxQuantity,
                `Based on your previous investments you may only purchase up to ${maxQunatityFormatted} shares and minimum shares amount for this offering is ${minUnits} shares.`,
              );
            }

            return schema.max(
              maxQuantity,
              `Based on your stated annual income and net worth, you can not invest in this offering. The minimum shares amount for this offering is ${minUnits} shares.`,
            );
          }

          if (investments.length > 0) {
            return schema.max(
              maxQuantity,
              `Based on your previous investments you may only purchase up to ${maxQunatityFormatted} shares.`,
            );
          }

          return schema.max(
            maxQuantity,
            `Based on your stated annual income and net worth, your number of shares must be ${maxQunatityFormatted} or less.`,
          );
        },
      )
      .max(
        MAX_PAYMENT_AMOUNT / unitPrice,
        `If you would like to invest more than ${CurrencyFormatter.format(
          MAX_PAYMENT_AMOUNT,
        )}, please contact the issuer at ${formatPhoneNumber(
          offering?.issuer?.phone,
        )}`,
      ),
    amount: Yup.number()
      .required("Investment amount is required")
      .min(
        minAmount,
        `Minimum ${CurrencyFormatter.format(minAmount)} investment required`,
      ),
    selectedBroker: Yup.object({
      label: Yup.string().nullable(),
      value: Yup.object().nullable(),
    }).nullable(),
  });

  return isOfferingRegA ? incomeSchema : standardSchema;
};
